import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import { monthlyCutsService, attendanceDashboardService } from '@/api/domains/attendance';
import type { LC_AllMonthlyCutoffItemOut, MonthlyCompanyCutoff } from '@/api/domains/attendance';
import type { RootState } from '@/store';


export interface UseMonthSummaryFiltersResult {
  selectedYear: string;
  selectedMonth: string;
  selectedCutId: string;
  lastOpenCutYear: string;
  lastOpenCutMonth: string;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  cutOptions: { value: string; label: string }[];
  isCutSelectorDisabled: boolean;
  setSelectedYear: (year: string) => void;
  setSelectedMonth: (month: string) => void;
  setSelectedCutId: (cutId: string) => void;
  isLoadingCutoffs: boolean;
  selectedCompanyId: string;
  setSelectedCompanyId: (companyId: string) => void;
  companyOptions: { value: string; label: string }[];
  companies: MonthlyCompanyCutoff[];
}

function findMostRecentCutoff(
  cutoffs: LC_AllMonthlyCutoffItemOut[],
): LC_AllMonthlyCutoffItemOut | null {
  if (!cutoffs.length) return null;
  return cutoffs.reduce((latest, current) => {
    if (current.year > latest.year) return current;
    if (current.year === latest.year && current.month > latest.month) return current;
    return latest;
  });
}

export const useMonthSummaryFilters = (enabled: boolean = true): UseMonthSummaryFiltersResult => {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const user = useSelector((state: RootState) => state.auth.user);

  // Empresas donde el colaborador tiene contrato: fuente de opciones del selector "Empresa".
  const [companies, setCompanies] = useState<MonthlyCompanyCutoff[]>([]);
  const [selectedCompanyId, setSelectedCompanyIdState] = useState('');

  const fetchCompanyOptions = useCallback(async () => {
    const response = await attendanceDashboardService.getMonthlyCompaniesSummary();
    if (!response.success || !response.data) {
      showApiError(response, translate('attendance:dashboard.monthSummary.filters.loadCompaniesError'));
      return;
    }
    const items = response.data;
    setCompanies(items);

    const ownCompanyId = user?.idEmpresa;
    const defaultCompany = items.find((c) => c.companyId === ownCompanyId) ?? items[0];
    if (defaultCompany) {
      setSelectedCompanyIdState(String(defaultCompany.companyId));
    }
  }, [showApiError, translate, user?.idEmpresa]);

  useEffect(() => {
    if (!enabled) return;
    void fetchCompanyOptions();
  }, [fetchCompanyOptions, enabled]);

  const companyOptions = useMemo(() => {
    const seen = new Set<number>();
    const options: { value: string; label: string }[] = [];
    for (const company of companies) {
      if (seen.has(company.companyId)) continue;
      seen.add(company.companyId);
      options.push({ value: String(company.companyId), label: company.companyName });
    }
    return options;
  }, [companies]);

  // --- Cortes mensuales (año/mes/corte) de la empresa seleccionada ---
  const [cutoffs, setCutoffs] = useState<LC_AllMonthlyCutoffItemOut[]>([]);
  const [selectedYear, setSelectedYearState] = useState('');
  const [selectedMonth, setSelectedMonthState] = useState('');
  const [selectedCutId, setSelectedCutIdState] = useState('');
  const [lastOpenCutYear, setLastOpenCutYear] = useState('');
  const [lastOpenCutMonth, setLastOpenCutMonth] = useState('');
  const [isLoadingCutoffs, setIsLoadingCutoffs] = useState(true);

  // Resetea síncronamente los filtros de corte de la empresa anterior: evita que
  // useAttendanceDashboard.fetchMonthly dispare con selectedCutId/selectedYear/selectedMonth
  // desalineados contra el nuevo cutoffCompanyId mientras fetchCutoffs sigue en vuelo.
  const setSelectedCompanyId = useCallback((companyId: string) => {
    setSelectedCompanyIdState(companyId);
    setSelectedYearState('');
    setSelectedMonthState('');
    setSelectedCutIdState('');
  }, []);

  const fetchCutoffs = useCallback(async (companyId: string) => {
    setIsLoadingCutoffs(true);
    try {
      const response = await monthlyCutsService.getAllMonthlyCutoffs(undefined, Number(companyId));
      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.monthSummary.filters.loadError'));
        return;
      }
      const items = response.data ?? [];
      setCutoffs(items);
      const mostRecent = findMostRecentCutoff(items);
      if (mostRecent) {
        setSelectedYearState(String(mostRecent.year));
        setSelectedMonthState(String(mostRecent.month));
        setSelectedCutIdState(String(mostRecent.monthlyCutId));
        setLastOpenCutYear(String(mostRecent.year));
        setLastOpenCutMonth(String(mostRecent.month));
      } else {
        setSelectedYearState('');
        setSelectedMonthState('');
        setSelectedCutIdState('');
        setLastOpenCutYear('');
        setLastOpenCutMonth('');
      }
    } finally {
      setIsLoadingCutoffs(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    void fetchCutoffs(selectedCompanyId);
  }, [fetchCutoffs, selectedCompanyId]);

  const yearOptions = useMemo(() => {
    const uniqueYears = [...new Set(cutoffs.map((c) => c.year))].sort((a, b) => b - a);
    return uniqueYears.map((year) => ({ value: String(year), label: String(year) }));
  }, [cutoffs]);

  const monthOptions = useMemo(() => {
    const monthsForYear = cutoffs
      .filter((c) => String(c.year) === selectedYear)
      .map((c) => c.month);
    const uniqueMonths = [...new Set(monthsForYear)].sort((a, b) => b - a);
    return uniqueMonths.map((month) => ({
      value: String(month),
      label: translate(`attendance:dashboard.months.${month - 1}`),
    }));
  }, [cutoffs, selectedYear, translate]);

  const cutOptions = useMemo(() =>
    cutoffs
      .filter((c) => String(c.year) === selectedYear && String(c.month) === selectedMonth)
      .map((c) => ({
        value: String(c.monthlyCutId),
        label: c.description ?? translate(`attendance:dashboard.months.${c.month - 1}`),
      })),
  [cutoffs, selectedYear, selectedMonth, translate]);

  const isCutSelectorDisabled = cutOptions.length <= 1;

  const setSelectedYear = useCallback((year: string) => {
    setSelectedYearState(year);
    const monthsForNewYear = cutoffs
      .filter((c) => String(c.year) === year)
      .map((c) => c.month)
      .sort((a, b) => b - a);
    if (monthsForNewYear.length > 0) {
      const newMonth = monthsForNewYear[0];
      setSelectedMonthState(String(newMonth));
      const firstCutForPeriod = cutoffs.find(
        (c) => String(c.year) === year && c.month === newMonth,
      );
      if (firstCutForPeriod) {
        setSelectedCutIdState(String(firstCutForPeriod.monthlyCutId));
      }
    }
  }, [cutoffs]);

  const setSelectedMonth = useCallback((month: string) => {
    setSelectedMonthState(month);
    const firstCutForPeriod = cutoffs.find(
      (c) => String(c.year) === selectedYear && String(c.month) === month,
    );
    if (firstCutForPeriod) {
      setSelectedCutIdState(String(firstCutForPeriod.monthlyCutId));
    }
  }, [cutoffs, selectedYear]);

  const setSelectedCutId = useCallback((cutId: string) => {
    setSelectedCutIdState(cutId);
  }, []);

  return {
    selectedYear,
    selectedMonth,
    selectedCutId,
    lastOpenCutYear,
    lastOpenCutMonth,
    yearOptions,
    monthOptions,
    cutOptions,
    isCutSelectorDisabled,
    setSelectedYear,
    setSelectedMonth,
    setSelectedCutId,
    isLoadingCutoffs,
    selectedCompanyId,
    setSelectedCompanyId,
    companyOptions,
    companies,
  };
};

export default useMonthSummaryFilters;
